import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useOutletContext } from 'react-router-dom';
import { collection, query, where, getDocs, getDoc, doc, setDoc, writeBatch } from 'firebase/firestore';
import { db, auth } from '../firebase';
import { isAdminRole, normaliseRole } from '../roles';
import { GraduationCap, Briefcase, Layers, Download, Upload, Plus, Trash2, Save, Settings as SettingsIcon, ShieldCheck, Lock } from 'lucide-react';
import Papa from 'papaparse';
import HeaderActionsPortal from './HeaderActionsPortal';
import { useT } from '../i18n.jsx';
import '../theme-palette.css';

const TEACHER_MODES = ['monthly', 'others'];
const STAFF_MODES = ['monthly', 'daily', 'hourly', 'others'];

const TEACHER_CSV_COLUMNS = ['month', 'initial', 'name', 'mode', 'salaryPoint', 'amount'];
const STAFF_CSV_COLUMNS = ['month', 'initial', 'name', 'mode', 'rate', 'unitQty', 'overtimeHours', 'overtimeRate', 'amount'];
const PAY_SCALE_CSV_COLUMNS = ['point', 'amount', 'remark'];

const num = (v) => {
  const n = parseFloat(v);
  return isNaN(n) ? 0 : n;
};

const money = (v) => num(v).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const todayLocal = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

const thisMonth = () => todayLocal().slice(0, 7);

const downloadCsv = (rows, columns, filename) => {
  const data = rows.length ? rows : [Object.fromEntries(columns.map(c => [c, '']))];
  const csv = Papa.unparse(data, { columns });
  // BOM so Excel opens the Chinese name columns in the correct encoding.
  const blob = new Blob([new Uint8Array([0xEF, 0xBB, 0xBF]), csv], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  link.click();
  URL.revokeObjectURL(url);
};

const SalaryAudit = () => {
  const { t } = useT();
  // The mode is stored on the salary record, decides which amount formula runs, and is matched
  // against the CSV on import -- so only the shown word is looked up.
  const modeWord = (m) => t(`salaryAudit.modes.${m}`, {}, m);
  // Which unit the "quantity" column counts for a given staff mode, and whether it applies at all.
  const unitLabel = (m) => t(`salaryAudit.units.${m}`, {}, '-');
  const { activeSchoolId, isMobile, userProfile, user } = useOutletContext();
  const [activeTab, setActiveTab] = useState('teacher');

  const ownEmail = (user?.email || '').toLowerCase();
  const isAdmin = isAdminRole(normaliseRole(userProfile?.role));

  // Who may open this module at all. Pay is confidential, so unlike most modules the default is
  // nobody: an admin has to name each person in Settings. Admins always retain access so a bad list
  // can never lock everyone out of payroll.
  const [authorizedStaff, setAuthorizedStaff] = useState([]);
  const [settingsLoaded, setSettingsLoaded] = useState(false);
  const [savingSettings, setSavingSettings] = useState(false);
  const [staffSearch, setStaffSearch] = useState('');

  const canUseModule = isAdmin || authorizedStaff.includes(ownEmail);

  const [academicYears, setAcademicYears] = useState([]);
  const [schoolYear, setSchoolYear] = useState('');
  const [people, setPeople] = useState([]);
  const [payScale, setPayScale] = useState([]);

  const [selectedMonth, setSelectedMonth] = useState(thisMonth);

  const [salaryRows, setSalaryRows] = useState({});

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  const teacherFileRef = useRef(null);
  const staffFileRef = useRef(null);
  const payScaleFileRef = useRef(null);

  const tabs = [
    { id: 'teacher', label: t('salaryAudit.tabs.teacher'), icon: GraduationCap },
    { id: 'staff', label: t('salaryAudit.tabs.staff'), icon: Briefcase },
    { id: 'payscale', label: t('salaryAudit.tabs.payscale'), icon: Layers },
    // Only admins decide who may see payroll -- an authorised user must not be able to add others.
    ...(isAdmin ? [{ id: 'settings', label: t('salaryAudit.tabs.settings'), icon: SettingsIcon }] : []),
  ];

  // --- Reference data -------------------------------------------------------

  useEffect(() => {
    if (!activeSchoolId) return;

    const loadReference = async () => {
      try {
        const ySnap = await getDocs(collection(db, 'schools', activeSchoolId, 'academic_years'));
        const yList = [];
        ySnap.forEach(d => yList.push({ uid: d.id, ...d.data() }));
        yList.sort((a, b) => (a.yearId || '').localeCompare(b.yearId || ''));
        setAcademicYears(yList);
        if (yList.length > 0) {
          const current = yList.find(y => y.cur_year) || yList[yList.length - 1];
          setSchoolYear(prev => prev || current.yearId);
        }
      } catch (e) {
        console.error('Error loading academic years', e);
      }

      try {
        const sSnap = await getDocs(collection(db, 'schools', activeSchoolId, 'staff_profiles'));
        const list = [];
        sSnap.forEach(d => {
          const data = d.data();
          if (!data.initial) return;
          list.push({
            initial: data.initial,
            name: `${data.lastName || ''} ${data.firstName || ''}`.trim() || data.initial,
            chineseName: data.chineseName || '',
            // staff_profiles doc IDs are the lowercased email everywhere in this app, which is what
            // the authorised-staff list and the Firestore rule both match on.
            email: (data.email || d.id || '').toLowerCase(),
            // Teaching vs non-teaching drives which salary tab a person appears on. `role` is the
            // system role set in Staff Management; jobRole is free text and can read "Non-teaching
            // Staff", so matching on it would misclassify.
            category: String(data.role || '').toLowerCase() === 'teacher' ? 'teacher' : 'staff',
            jobRole: data.jobRole || '',
          });
        });
        list.sort((a, b) => a.initial.localeCompare(b.initial));
        setPeople(list);
      } catch (e) {
        console.error('Error loading staff profiles', e);
      }

      // Same as Notice & Payment: the authorised list is metadata/module_admins.salary_audit, which
      // is what the rules test. Salary figures are the most sensitive data here, so the screen and
      // the rule must not be reading two different lists.
      try {
        const sSnap = await getDoc(doc(db, 'schools', activeSchoolId, 'metadata', 'module_admins'));
        const data = sSnap.exists() ? sSnap.data() : {};
        setAuthorizedStaff((data.salary_audit || []).map(e => String(e).toLowerCase()));
      } catch (e) {
        console.error('Error loading module admins', e);
      }
      setSettingsLoaded(true);

      try {
        const pSnap = await getDocs(collection(db, 'schools', activeSchoolId, 'pay_scale'));
        const list = [];
        pSnap.forEach(d => list.push({ uid: d.id, ...d.data() }));
        list.sort((a, b) => num(a.point) - num(b.point));
        setPayScale(list);
      } catch (e) {
        console.error('Error loading pay scale', e);
      }
    };

    loadReference();
  }, [activeSchoolId]);

  const payScaleMap = useMemo(() => {
    const m = {};
    payScale.forEach(p => { if (p.point !== '' && p.point != null) m[String(p.point)] = p; });
    return m;
  }, [payScale]);

  const teachers = useMemo(() => people.filter(p => p.category === 'teacher'), [people]);
  const staff = useMemo(() => people.filter(p => p.category === 'staff'), [people]);

  // --- Salary ---------------------------------------------------------------

  const loadSalary = useCallback(async () => {
    if (!activeSchoolId || !selectedMonth) return;
    setLoading(true);
    try {
      const snap = await getDocs(query(
        collection(db, 'schools', activeSchoolId, 'salary_record'),
        where('month', '==', selectedMonth)
      ));
      const map = {};
      snap.forEach(d => { map[d.data().initial] = d.data(); });
      setSalaryRows(map);
    } catch (e) {
      console.error('Error loading salary records', e);
      alert(t('salaryAudit.loadFailed', { message: e.message }));
    }
    setLoading(false);
  }, [activeSchoolId, selectedMonth]);

  useEffect(() => {
    if (activeTab === 'teacher' || activeTab === 'staff') loadSalary();
  }, [activeTab, loadSalary]);

  const rowFor = (p) => salaryRows[p.initial] || {};

  const updateSalaryRow = (initial, patch) => {
    setSalaryRows(prev => ({ ...prev, [initial]: { ...(prev[initial] || {}), ...patch } }));
  };

  const teacherAmount = useCallback((row) => {
    if ((row.mode || 'monthly') === 'others') return num(row.manualAmount);
    const point = payScaleMap[String(row.salaryPoint)];
    return point ? num(point.amount) : 0;
  }, [payScaleMap]);

  const staffAmount = useCallback((row) => {
    const mode = row.mode || 'monthly';
    const rate = num(row.rate);
    let base;
    if (mode === 'monthly') base = rate;
    else if (mode === 'daily' || mode === 'hourly') base = rate * num(row.unitQty);
    else base = num(row.manualAmount);
    return base + num(row.overtimeHours) * num(row.overtimeRate);
  }, []);

  const handleSaveSalary = async (category) => {
    if (!activeSchoolId || !selectedMonth) return;
    setSaving(true);
    try {
      const batch = writeBatch(db);
      const list = category === 'teacher' ? teachers : staff;
      const now = new Date().toISOString();

      list.forEach(p => {
        const row = rowFor(p);
        const mode = row.mode || 'monthly';
        const amount = category === 'teacher' ? teacherAmount(row) : staffAmount(row);
        batch.set(doc(db, 'schools', activeSchoolId, 'salary_record', `${selectedMonth}__${p.initial}`), {
          month: selectedMonth,
          schYr: schoolYear,
          initial: p.initial,
          name: p.name,
          category,
          mode,
          salaryPoint: row.salaryPoint ?? '',
          rate: num(row.rate),
          unitQty: num(row.unitQty),
          overtimeHours: num(row.overtimeHours),
          overtimeRate: num(row.overtimeRate),
          manualAmount: num(row.manualAmount),
          amount,
          updatedAt: now,
          updatedBy: auth.currentUser?.email || '',
        }, { merge: true });
      });

      await batch.commit();
      await loadSalary();
      alert(t('salaryAudit.salarySaved'));
    } catch (e) {
      console.error('Error saving salary', e);
      alert(t('salaryAudit.saveFailed', { message: e.message }));
    }
    setSaving(false);
  };

  // --- Pay scale ------------------------------------------------------------

  const addPayScaleRow = () => {
    setPayScale(prev => [...prev, { uid: `new_${Date.now()}_${Math.random().toString(36).slice(2, 7)}`, point: '', amount: 0, remark: '', isNew: true }]);
  };

  const updatePayScaleRow = (uid, patch) => {
    setPayScale(prev => prev.map(p => (p.uid === uid ? { ...p, ...patch } : p)));
  };

  const removePayScaleRow = (uid) => {
    setPayScale(prev => prev.filter(p => p.uid !== uid));
  };

  const handleSavePayScale = async () => {
    if (!activeSchoolId) return;
    const points = payScale.map(p => String(p.point).trim()).filter(Boolean);
    if (points.length !== new Set(points).size) {
      alert(t('salaryAudit.pointsMustBeUnique'));
      return;
    }
    if (payScale.some(p => String(p.point).trim() === '')) {
      alert(t('salaryAudit.everyRowNeedsPoint'));
      return;
    }

    setSaving(true);
    try {
      const existing = await getDocs(collection(db, 'schools', activeSchoolId, 'pay_scale'));
      const keep = new Set(payScale.filter(p => !p.isNew).map(p => p.uid));
      const batch = writeBatch(db);

      existing.forEach(d => { if (!keep.has(d.id)) batch.delete(d.ref); });

      payScale.forEach(p => {
        const ref = p.isNew
          ? doc(collection(db, 'schools', activeSchoolId, 'pay_scale'))
          : doc(db, 'schools', activeSchoolId, 'pay_scale', p.uid);
        batch.set(ref, {
          point: String(p.point).trim(),
          amount: num(p.amount),
          remark: p.remark || '',
        });
      });

      await batch.commit();

      const snap = await getDocs(collection(db, 'schools', activeSchoolId, 'pay_scale'));
      const list = [];
      snap.forEach(d => list.push({ uid: d.id, ...d.data() }));
      list.sort((a, b) => num(a.point) - num(b.point));
      setPayScale(list);
      alert(t('salaryAudit.payScaleSaved'));
    } catch (e) {
      console.error('Error saving pay scale', e);
      alert(t('salaryAudit.saveFailed', { message: e.message }));
    }
    setSaving(false);
  };

  // --- Settings -------------------------------------------------------------


  // --- Imports --------------------------------------------------------------
  //
  // All three importers merge into local state and leave persistence to the tab's own Save button,
  // matching the CSV import elsewhere in the app: an operator can eyeball a payroll file (and undo
  // it by switching months / reloading) before anything is written.

  const parseCsv = (file) => new Promise((resolve, reject) => {
    Papa.parse(file, { header: true, skipEmptyLines: true, complete: resolve, error: reject });
  });

  // Trims both keys and values so a header of " initial " or a value padded by Excel still matches.
  const cleanRow = (raw) =>
    Object.fromEntries(Object.entries(raw).map(([k, v]) => [(k || '').trim(), String(v ?? '').trim()]));

  // Salary rows are keyed on `initial`, and an initial that doesn't match a current profile is
  // skipped rather than created -- a payroll file must never invent a person. `month` in the file is
  // ignored on purpose: the import always applies to the month currently selected on screen, so a
  // stale header can't silently write to the wrong payroll period.
  const handleImportSalary = async (category, e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;

    const list = category === 'teacher' ? teachers : staff;
    const modes = category === 'teacher' ? TEACHER_MODES : STAFF_MODES;
    const known = new Map(list.map(p => [p.initial.toLowerCase(), p.initial]));

    try {
      const results = await parseCsv(file);
      if (!results.data.length) { alert(t('salaryAudit.csvNoRows')); return; }

      let updated = 0;
      const unknown = [];

      setSalaryRows(prev => {
        const next = { ...prev };
        results.data.forEach(raw => {
          const rec = cleanRow(raw);
          const initial = known.get((rec.initial || '').toLowerCase());
          if (!initial) {
            if (rec.initial) unknown.push(rec.initial);
            return;
          }

          const mode = modes.includes((rec.mode || '').toLowerCase()) ? rec.mode.toLowerCase() : 'monthly';
          const patch = { mode };

          if (category === 'teacher') {
            patch.salaryPoint = rec.salaryPoint ?? '';
            // Only meaningful on 'others'; for a scale-based row the amount is derived, so importing
            // it would let a stale figure disagree with the pay scale.
            if (mode === 'others') patch.manualAmount = num(rec.amount);
          } else {
            if (mode === 'others') {
              patch.manualAmount = num(rec.amount);
            } else {
              patch.rate = num(rec.rate);
              patch.unitQty = num(rec.unitQty);
            }
            patch.overtimeHours = num(rec.overtimeHours);
            patch.overtimeRate = num(rec.overtimeRate);
          }

          next[initial] = { ...(next[initial] || {}), ...patch };
          updated += 1;
        });
        return next;
      });

      // Whole sentences joined by newlines, not one sentence assembled from fragments.
      const lines = [t('salaryAudit.importedSalary', { count: updated, month: selectedMonth })];
      if (unknown.length) {
        lines.push(t('salaryAudit.importedSkipped', {
          count: unknown.length,
          names: [...new Set(unknown)].slice(0, 10).join(', '),
        }));
      }
      alert(`${lines.join('\n')}\n\n${t('salaryAudit.clickSaveToPersist')}`);
    } catch (err) {
      console.error('Error importing salary CSV', err);
      alert(t('salaryAudit.importFailed', { message: err.message }));
    }
  };

  // Merges by salary point: a matching point updates in place, a new one is appended.
  const handleImportPayScale = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;

    try {
      const results = await parseCsv(file);
      if (!results.data.length) { alert(t('salaryAudit.csvNoRows')); return; }

      let added = 0, updated = 0, skipped = 0;

      setPayScale(prev => {
        const next = [...prev];
        results.data.forEach(raw => {
          const rec = cleanRow(raw);
          const point = rec.point || '';
          if (!point) { skipped += 1; return; }

          const idx = next.findIndex(p => String(p.point).trim().toLowerCase() === point.toLowerCase());
          if (idx >= 0) {
            next[idx] = { ...next[idx], point, amount: num(rec.amount), remark: rec.remark || '' };
            updated += 1;
          } else {
            next.push({
              uid: `new_${Date.now()}_${Math.random().toString(36).slice(2, 7)}`,
              point,
              amount: num(rec.amount),
              remark: rec.remark || '',
              isNew: true,
            });
            added += 1;
          }
        });
        next.sort((a, b) => num(a.point) - num(b.point));
        return next;
      });

      const lines = [t('salaryAudit.importedPayScale', { added, updated })];
      if (skipped) lines.push(t('salaryAudit.importedPayScaleSkipped', { count: skipped }));
      alert(`${lines.join('\n')}\n\n${t('salaryAudit.clickSaveToPersist')}`);
    } catch (err) {
      console.error('Error importing pay scale CSV', err);
      alert(t('salaryAudit.importFailed', { message: err.message }));
    }
  };

  // --- Exports --------------------------------------------------------------

  const exportTeacherSalary = () => downloadCsv(
    teachers.map(p => {
      const row = rowFor(p);
      return {
        month: selectedMonth, initial: p.initial, name: p.name,
        mode: row.mode || 'monthly', salaryPoint: row.salaryPoint ?? '',
        amount: teacherAmount(row).toFixed(2),
      };
    }),
    TEACHER_CSV_COLUMNS,
    `teacher_salary_${selectedMonth}.csv`
  );

  const exportStaffSalary = () => downloadCsv(
    staff.map(p => {
      const row = rowFor(p);
      return {
        month: selectedMonth, initial: p.initial, name: p.name,
        mode: row.mode || 'monthly', rate: num(row.rate).toFixed(2),
        unitQty: num(row.unitQty), overtimeHours: num(row.overtimeHours),
        overtimeRate: num(row.overtimeRate).toFixed(2),
        amount: staffAmount(row).toFixed(2),
      };
    }),
    STAFF_CSV_COLUMNS,
    `staff_salary_${selectedMonth}.csv`
  );

  const exportPayScale = () => downloadCsv(
    payScale.map(p => ({ point: p.point, amount: num(p.amount).toFixed(2), remark: p.remark || '' })),
    PAY_SCALE_CSV_COLUMNS,
    'pay_scale.csv'
  );

  // --- Shared styles --------------------------------------------------------

  const cardStyle = {
    backgroundColor: 'var(--color-bg-card)',
    borderRadius: isMobile ? 0 : 'var(--radius-lg)',
    boxShadow: isMobile ? 'none' : 'var(--shadow-sm)',
    overflowX: 'auto',
  };
  const thStyle = { padding: '0.65rem 0.75rem', fontWeight: '600', whiteSpace: 'nowrap' };
  const tdStyle = { padding: '0.5rem 0.75rem', borderTop: '1px solid var(--color-border)', whiteSpace: 'nowrap' };
  const headRowStyle = {
    backgroundColor: 'var(--color-bg-app)',
    borderBottom: '1px solid var(--color-border)',
    fontSize: '0.8rem',
    color: 'var(--color-text-muted)',
    textAlign: 'left',
  };
  const cellInput = { margin: 0, padding: '0.35rem 0.5rem', height: 'auto', fontSize: '0.85rem' };

  const totalFor = (list, fn) => list.reduce((sum, p) => sum + fn(rowFor(p)), 0);

  const emptyState = (message) => (
    <div style={{ padding: '3rem', textAlign: 'center', color: 'var(--color-text-muted)' }}>{message}</div>
  );

  // Gate the whole module, not just the sidebar link. Waits for the access list to load first so an
  // authorised user never sees a "no access" flash on the way in.
  if (!settingsLoaded) {
    return (
      <div className="animate-fade-in new-theme" style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <span style={{ color: 'var(--color-text-muted)' }}>{t('common.loading')}</span>
      </div>
    );
  }

  if (!canUseModule) {
    return (
      <div className="animate-fade-in new-theme" style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '2rem' }}>
        <div style={{ textAlign: 'center', maxWidth: '420px', color: 'var(--color-text-muted)' }}>
          <Lock size={40} style={{ opacity: 0.25, marginBottom: '1rem' }} />
          <h2 style={{ fontSize: '1.15rem', fontWeight: '700', margin: '0 0 0.5rem', color: 'var(--color-text-main)' }}>{t('salaryAudit.noAccessTitle')}</h2>
          <p style={{ margin: 0, fontSize: '0.9rem' }}>
            {t('salaryAudit.noAccessBody')}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="animate-fade-in new-theme" style={{ width: '100%', height: '100%', display: 'flex', flexDirection: 'column' }}>
      <div className="theme-tabs-bar" style={{ minHeight: '66px' }}>
        <div className="theme-tabs-group">
          {tabs.map(tab => {
            const Icon = tab.icon;
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`theme-tab ${activeTab === tab.id ? 'active' : ''}`}
                style={{ whiteSpace: 'nowrap' }}
              >
                <div className="theme-tab-icon"><Icon size={18} /></div>
                {tab.label}
              </button>
            );
          })}
        </div>

        <HeaderActionsPortal>
          <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
            {(activeTab === 'teacher' || activeTab === 'staff') && (
              <>
                <input
                  type="month"
                  className="input-field"
                  value={selectedMonth}
                  onChange={e => setSelectedMonth(e.target.value)}
                  style={{ width: '150px', margin: 0 }}
                />
                <input
                  type="file"
                  accept=".csv"
                  style={{ display: 'none' }}
                  ref={activeTab === 'teacher' ? teacherFileRef : staffFileRef}
                  onChange={e => handleImportSalary(activeTab, e)}
                />
                <button
                  className="btn-secondary"
                  onClick={() => (activeTab === 'teacher' ? teacherFileRef : staffFileRef).current?.click()}
                  title={t('salaryAudit.importCsv')}
                  style={{ padding: '0.5rem 0.7rem' }}
                >
                  <Upload size={16} />
                </button>
                <button
                  className="btn-secondary"
                  onClick={activeTab === 'teacher' ? exportTeacherSalary : exportStaffSalary}
                  title={t('salaryAudit.exportForBanking')}
                  style={{ padding: '0.5rem 0.7rem' }}
                >
                  <Download size={16} />
                </button>
                <button className="btn-primary" onClick={() => handleSaveSalary(activeTab)} disabled={saving} style={{ width: 'auto', padding: '0 0.9rem', whiteSpace: 'nowrap' }}>
                  <Save size={15} /> {t('common.save')}
                </button>
              </>
            )}

            {activeTab === 'payscale' && (
              <>
                <input type="file" accept=".csv" style={{ display: 'none' }} ref={payScaleFileRef} onChange={handleImportPayScale} />
                <button className="btn-secondary" onClick={() => payScaleFileRef.current?.click()} title={t('salaryAudit.importCsv')} style={{ padding: '0.5rem 0.7rem' }}>
                  <Upload size={16} />
                </button>
                <button className="btn-secondary" onClick={exportPayScale} title={t('salaryAudit.exportCsv')} style={{ padding: '0.5rem 0.7rem' }}>
                  <Download size={16} />
                </button>
                <button className="btn-secondary" onClick={addPayScaleRow} style={{ width: 'auto', padding: '0 0.9rem', whiteSpace: 'nowrap' }}>
                  <Plus size={15} /> {t('salaryAudit.addPoint')}
                </button>
                <button className="btn-primary" onClick={handleSavePayScale} disabled={saving} style={{ width: 'auto', padding: '0 0.9rem', whiteSpace: 'nowrap' }}>
                  <Save size={15} /> {t('common.save')}
                </button>
              </>
            )}
          </div>
        </HeaderActionsPortal>
      </div>

      <div style={{ flex: 1, overflowY: 'auto', padding: isMobile ? '0 0 1rem' : '0 1.5rem 1.5rem' }}>

        {/* ---------------- TEACHER SALARY ---------------- */}
        {activeTab === 'teacher' && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
            <div style={{ padding: isMobile ? '0.75rem 0.75rem 0' : 0 }}>
              <h2 style={{ fontSize: '1.25rem', fontWeight: '700', margin: 0 }}>{t('salaryAudit.teacherHeading', { month: selectedMonth })}</h2>
              {payScale.length === 0 && (
                <p style={{ color: 'var(--color-danger)', fontSize: '0.85rem', margin: '0.5rem 0 0' }}>
                  {t('salaryAudit.noPayScale')}
                </p>
              )}
            </div>

            <div style={cardStyle}>
              {loading ? emptyState(t('common.loading')) : teachers.length === 0 ? emptyState(t('salaryAudit.noTeachers')) : (
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.875rem' }}>
                  <thead>
                    <tr style={headRowStyle}>
                      <th style={thStyle}>{t('salaryAudit.initial')}</th>
                      <th style={thStyle}>{t('common.name')}</th>
                      <th style={thStyle}>{t('salaryAudit.mode')}</th>
                      <th style={thStyle}>{t('salaryAudit.salaryPoint')}</th>
                      <th style={{ ...thStyle, textAlign: 'right' }}>{t('salaryAudit.amount')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {teachers.map(p => {
                      const row = rowFor(p);
                      const mode = row.mode || 'monthly';
                      return (
                        <tr key={p.initial}>
                          <td style={{ ...tdStyle, fontWeight: '600' }}>{p.initial}</td>
                          <td style={tdStyle}>{p.name}</td>
                          <td style={tdStyle}>
                            <select className="input-field" style={{ ...cellInput, width: '110px' }} value={mode} onChange={e => updateSalaryRow(p.initial, { mode: e.target.value })}>
                              {TEACHER_MODES.map(m => <option key={m} value={m}>{modeWord(m)}</option>)}
                            </select>
                          </td>
                          <td style={tdStyle}>
                            {mode === 'monthly' ? (
                              <select className="input-field" style={{ ...cellInput, width: '130px' }} value={row.salaryPoint ?? ''} onChange={e => updateSalaryRow(p.initial, { salaryPoint: e.target.value })}>
                                <option value="">{t('salaryAudit.selectPlaceholder')}</option>
                                {payScale.map(ps => (
                                  <option key={ps.uid} value={ps.point}>{ps.point} ({money(ps.amount)})</option>
                                ))}
                              </select>
                            ) : (
                              <span style={{ color: 'var(--color-text-muted)' }}>{t('salaryAudit.manual')}</span>
                            )}
                          </td>
                          <td style={{ ...tdStyle, textAlign: 'right' }}>
                            {mode === 'others' ? (
                              <input
                                type="number" step="0.01" className="input-field"
                                style={{ ...cellInput, width: '130px', textAlign: 'right' }}
                                value={row.manualAmount ?? ''}
                                onChange={e => updateSalaryRow(p.initial, { manualAmount: e.target.value })}
                                placeholder="0.00"
                              />
                            ) : (
                              <strong>{money(teacherAmount(row))}</strong>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                  <tfoot>
                    <tr style={{ borderTop: '2px solid var(--color-border)', fontWeight: '700' }}>
                      <td style={tdStyle} colSpan={4}>{t('salaryAudit.totalTeachers', { count: teachers.length })}</td>
                      <td style={{ ...tdStyle, textAlign: 'right' }}>{money(totalFor(teachers, teacherAmount))}</td>
                    </tr>
                  </tfoot>
                </table>
              )}
            </div>
          </div>
        )}

        {/* ---------------- STAFF SALARY ---------------- */}
        {activeTab === 'staff' && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
            <div style={{ padding: isMobile ? '0.75rem 0.75rem 0' : 0 }}>
              <h2 style={{ fontSize: '1.25rem', fontWeight: '700', margin: 0 }}>{t('salaryAudit.staffHeading', { month: selectedMonth })}</h2>
            </div>

            <div style={cardStyle}>
              {loading ? emptyState(t('common.loading')) : staff.length === 0 ? emptyState(t('salaryAudit.noStaff')) : (
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.875rem' }}>
                  <thead>
                    <tr style={headRowStyle}>
                      <th style={thStyle}>{t('salaryAudit.initial')}</th>
                      <th style={thStyle}>{t('common.name')}</th>
                      <th style={thStyle}>{t('salaryAudit.mode')}</th>
                      <th style={{ ...thStyle, textAlign: 'right' }}>{t('salaryAudit.basicRate')}</th>
                      <th style={{ ...thStyle, textAlign: 'right' }}>{t('salaryAudit.qty')}</th>
                      <th style={{ ...thStyle, textAlign: 'right' }}>{t('salaryAudit.otHours')}</th>
                      <th style={{ ...thStyle, textAlign: 'right' }}>{t('salaryAudit.otRate')}</th>
                      <th style={{ ...thStyle, textAlign: 'right' }}>{t('salaryAudit.amount')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {staff.map(p => {
                      const row = rowFor(p);
                      const mode = row.mode || 'monthly';
                      const usesQty = mode === 'daily' || mode === 'hourly';
                      return (
                        <tr key={p.initial}>
                          <td style={{ ...tdStyle, fontWeight: '600' }}>{p.initial}</td>
                          <td style={tdStyle}>{p.name}</td>
                          <td style={tdStyle}>
                            <select className="input-field" style={{ ...cellInput, width: '105px' }} value={mode} onChange={e => updateSalaryRow(p.initial, { mode: e.target.value })}>
                              {STAFF_MODES.map(m => <option key={m} value={m}>{modeWord(m)}</option>)}
                            </select>
                          </td>
                          <td style={{ ...tdStyle, textAlign: 'right' }}>
                            {mode === 'others' ? (
                              <input type="number" step="0.01" className="input-field" style={{ ...cellInput, width: '120px', textAlign: 'right' }} value={row.manualAmount ?? ''} onChange={e => updateSalaryRow(p.initial, { manualAmount: e.target.value })} placeholder="0.00" />
                            ) : (
                              <input type="number" step="0.01" className="input-field" style={{ ...cellInput, width: '120px', textAlign: 'right' }} value={row.rate ?? ''} onChange={e => updateSalaryRow(p.initial, { rate: e.target.value })} placeholder="0.00" />
                            )}
                          </td>
                          <td style={{ ...tdStyle, textAlign: 'right' }}>
                            {usesQty ? (
                              <input type="number" step="0.5" className="input-field" style={{ ...cellInput, width: '90px', textAlign: 'right' }} value={row.unitQty ?? ''} onChange={e => updateSalaryRow(p.initial, { unitQty: e.target.value })} placeholder={unitLabel(mode)} />
                            ) : (
                              <span style={{ color: 'var(--color-text-muted)' }}>—</span>
                            )}
                          </td>
                          <td style={{ ...tdStyle, textAlign: 'right' }}>
                            <input type="number" step="0.5" className="input-field" style={{ ...cellInput, width: '90px', textAlign: 'right' }} value={row.overtimeHours ?? ''} onChange={e => updateSalaryRow(p.initial, { overtimeHours: e.target.value })} placeholder="0" />
                          </td>
                          <td style={{ ...tdStyle, textAlign: 'right' }}>
                            <input type="number" step="0.01" className="input-field" style={{ ...cellInput, width: '100px', textAlign: 'right' }} value={row.overtimeRate ?? ''} onChange={e => updateSalaryRow(p.initial, { overtimeRate: e.target.value })} placeholder="0.00" />
                          </td>
                          <td style={{ ...tdStyle, textAlign: 'right' }}><strong>{money(staffAmount(row))}</strong></td>
                        </tr>
                      );
                    })}
                  </tbody>
                  <tfoot>
                    <tr style={{ borderTop: '2px solid var(--color-border)', fontWeight: '700' }}>
                      <td style={tdStyle} colSpan={7}>{t('salaryAudit.totalStaff', { count: staff.length })}</td>
                      <td style={{ ...tdStyle, textAlign: 'right' }}>{money(totalFor(staff, staffAmount))}</td>
                    </tr>
                  </tfoot>
                </table>
              )}
            </div>
          </div>
        )}

        {/* ---------------- PAY SCALE ---------------- */}
        {activeTab === 'payscale' && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
            <div style={{ padding: isMobile ? '0.75rem 0.75rem 0' : 0 }}>
              <h2 style={{ fontSize: '1.25rem', fontWeight: '700', margin: 0 }}>{t('salaryAudit.payScaleTitle')}</h2>
              <p style={{ color: 'var(--color-text-muted)', fontSize: '0.85rem', margin: '0.35rem 0 0' }}>
                {t('salaryAudit.payScaleIntro')}
              </p>
            </div>

            <div style={cardStyle}>
              {payScale.length === 0 ? emptyState(t('salaryAudit.noPoints')) : (
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.875rem' }}>
                  <thead>
                    <tr style={headRowStyle}>
                      <th style={thStyle}>{t('salaryAudit.salaryPoint')}</th>
                      <th style={{ ...thStyle, textAlign: 'right' }}>{t('salaryAudit.amount')}</th>
                      <th style={thStyle}>{t('salaryAudit.remark')}</th>
                      <th style={thStyle}></th>
                    </tr>
                  </thead>
                  <tbody>
                    {payScale.map(p => (
                      <tr key={p.uid}>
                        <td style={tdStyle}>
                          <input className="input-field" style={{ ...cellInput, width: '120px' }} value={p.point ?? ''} onChange={e => updatePayScaleRow(p.uid, { point: e.target.value })} placeholder={t('salaryAudit.pointPlaceholder')} />
                        </td>
                        <td style={{ ...tdStyle, textAlign: 'right' }}>
                          <input type="number" step="0.01" className="input-field" style={{ ...cellInput, width: '150px', textAlign: 'right' }} value={p.amount ?? ''} onChange={e => updatePayScaleRow(p.uid, { amount: e.target.value })} placeholder="0.00" />
                        </td>
                        <td style={tdStyle}>
                          <input className="input-field" style={{ ...cellInput, width: '100%', minWidth: '160px' }} value={p.remark ?? ''} onChange={e => updatePayScaleRow(p.uid, { remark: e.target.value })} placeholder="—" />
                        </td>
                        <td style={{ ...tdStyle, textAlign: 'right' }}>
                          <button onClick={() => removePayScaleRow(p.uid)} title={t('common.remove')} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--color-danger)', padding: '0.25rem' }}>
                            <Trash2 size={16} />
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        )}

        {/* ---------------- SETTINGS ---------------- */}
        {activeTab === 'settings' && isAdmin && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
            <div style={{ padding: isMobile ? '0.75rem 0.75rem 0' : 0 }}>
              <h2 style={{ fontSize: '1.25rem', fontWeight: '700', margin: 0 }}>{t('salaryAudit.moduleAccess')}</h2>
              <p style={{ color: 'var(--color-text-muted)', fontSize: '0.85rem', margin: '0.35rem 0 0' }}>
                {t('salaryAudit.moduleAccessIntro')}
              </p>
            </div>

            <div style={{ ...cardStyle, overflowX: 'visible', padding: isMobile ? '0.75rem' : '1.25rem', display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', flexWrap: 'wrap' }}>
                <ShieldCheck size={16} color="var(--color-primary)" />
                <strong style={{ fontSize: '0.9rem' }}>{t('salaryAudit.authorisedStaff', { count: authorizedStaff.length })}</strong>
                <span style={{ fontSize: '0.85rem', color: 'var(--color-text-muted)' }}>
                  {authorizedStaff.length === 0
                    ? t('salaryAudit.noneAuthorised')
                    : people.filter(p => authorizedStaff.includes(p.email)).map(p => p.initial).join(', ')}
                </span>
              </div>

              <p style={{ fontSize: '0.8rem', color: 'var(--color-text-muted)', margin: 0, lineHeight: 1.6 }}>
                {t('salaryAudit.referenceOnly')}
              </p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default SalaryAudit;
